async function fetchAllCollectionProducts(collectionHandle) {
  const accessToken = window.accessToken;
  const shopDomain = window.shopDomain;
  const allProducts = [];
  let hasNextPage = true;
  let endCursor = null;

  while (hasNextPage) {
    const query = `
        query($handle: String!, $cursor: String, $language: LanguageCode) @inContext(language: $language) {
          collection(handle: $handle) {
            products(first: 50, after: $cursor) {
              pageInfo { hasNextPage endCursor }
              edges {
                node {
                  id
                  title 
                  handle
                  options { name }
                  metafields(identifiers: [
                    {namespace: "custom", key: "discount"},
                    {namespace: "custom", key: "filter_product_category"},
                    {namespace: "custom", key: "basic_size_variant_image_plp"},
                    {namespace: "reviewscouk", key: "rating"},
                    {namespace: "custom", key: "collection_name"}
                  ]) {
                    key
                    value
                    type
                    reference {
                      ... on Metaobject {
                        id
                        handle
                        fields { key value }
                      }
                    }
                  }
                  variants(first: 100) {
                    pageInfo { hasNextPage endCursor }
                    edges {
                      node {
                        id
                        title
                        availableForSale
                        price { amount currencyCode }
                        compareAtPrice { amount }
                        image { url }
                        selectedOptions { name value }
                        metafields(identifiers: [
                          {namespace: "custom", key: "variant_badges"},
                          {namespace: "filtering", key: "newest_priority"},
                          {namespace: "custom", key: "hide_on_plp"}
                        ]) { 
                          key 
                          value 
                          type 
                          reference {
                            ... on Metaobject {
                              id
                              handle
                              fields { key value }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      `;

    const response = await fetch(
      `https://${shopDomain}/api/2025-07/graphql.json`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": accessToken,
        },
        body: JSON.stringify({
          query,
          variables: { handle: collectionHandle, cursor: endCursor, language: window.shopLocale || 'EN' },
        }),
      }
    );

    const json = await response.json();
    const productEdges = json?.data?.collection?.products?.edges || [];

    for (const edge of productEdges) {
      const product = edge.node;
      product.allVariants = [...product.variants.edges];

      let variantHasNext = product.variants.pageInfo.hasNextPage;
      let variantCursor = product.variants.pageInfo.endCursor;

      while (variantHasNext) {
        const variantQuery = `
            query($id: ID!, $cursor: String, $language: LanguageCode) @inContext(language: $language) {
              product(id: $id) {
                variants(first: 100, after: $cursor) {
                  pageInfo { hasNextPage endCursor }
                  edges {
                    node {
                      id
                      title
                      availableForSale
                      price { amount currencyCode }
                      compareAtPrice { amount }
                      image { url }
                      selectedOptions { name value }
                      metafields(identifiers: [
                        {namespace: "custom", key: "variant_badges"},
                        {namespace: "filtering", key: "newest_priority"},
                        {namespace: "custom", key: "hide_on_plp"}
                      ]) { 
                        key 
                        value 
                        type 
                        reference {
                          ... on Metaobject {
                            id
                            handle
                            fields { key value }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          `;
        const variantResponse = await fetch(
          `https://${shopDomain}/api/2025-07/graphql.json`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Shopify-Storefront-Access-Token": accessToken,
            },
            body: JSON.stringify({
              query: variantQuery,
              variables: { id: product.id, cursor: variantCursor, language: window.shopLocale || 'EN' },
            }),
          }
        );

        const variantJson = await variantResponse.json();
        const variantData = variantJson?.data?.product?.variants;
        if (!variantData) break;
        product.allVariants.push(...variantData.edges);
        variantHasNext = variantData.pageInfo.hasNextPage;
        variantCursor = variantData.pageInfo.endCursor;
      }

      allProducts.push(product);
    }

    hasNextPage =
      json?.data?.collection?.products?.pageInfo?.hasNextPage;
    endCursor = json?.data?.collection?.products?.pageInfo?.endCursor;
  }

  return allProducts;
}

function normalizeImage(url) {
  if (!url) return "";
  const match = url.match(/\/([^/]+\.(?:jpg|jpeg|png|webp))(?:\?v=\d+)?$/i);
  if (match) {
    const query = url.includes("?v=") ? url.split("?v=")[1] : "";
    return `files/${match[1]}${query ? "?v=" + query : ""}`;
  }
  return url
    .replace(/^https?:\/\/cdn\.shopify\.com\/s\/files\/.*\/files\//, "files/")
    .replace(/^https?:\/\/[^/]+\/cdn\/shop\/files\//, "files/")
    .replace(/files\/files\//g, "files/");
}

function getMetafieldValue(metafields, key) {
  if (!Array.isArray(metafields)) return null;
  const valid = metafields.filter(Boolean);
  const field = valid.find((m) => m && m.key === key);
  return field ? field.value : null;
}

function formatPrice(amount, currency) {
  const num = Number(amount);
  const moneyInput = document.querySelector(".shop_all-cards-data[data-price]");
  if (!moneyInput) {
    const symbol =
      currency === "EUR"
        ? "€"
        : currency === "GBP"
          ? "£"
          : currency === "USD"
            ? "$"
            : currency;
    return `${symbol}${num.toFixed(0)}`;
  }
  const sample = moneyInput.getAttribute("data-price");
  return sample.replace(/0[.,]00|0[.,]?/, num.toFixed(0));
}

function parseBadgeMetafield(metafields) {
  if (!Array.isArray(metafields) || !metafields.length) return [];
  const badgeField = metafields.find((m) => m && m.key === "variant_badges");
  if (!badgeField) return [];
  const badgeInput = document.querySelector(".shop_all-cards-data[data-badge]");
  const badgeValue = badgeInput?.getAttribute("data-badge")?.trim();
  if (!badgeValue) return [];
  return [
    { name: badgeValue, handle: badgeValue.toLowerCase().replace(/\s+/g, "-") },
  ];
}

function buildVariantsArray(products) {
  const allVariants = [];

  for (const product of products) {
    const productMetafields = (product.metafields || []).filter(Boolean);
    const productMeta = Object.fromEntries(
      productMetafields.filter((m) => m && m.key).map((m) => [m.key, m.value])
    );

    const discountValue = productMeta.discount
      ? Number(productMeta.discount) * 100
      : 0;
    const rating = Number(productMeta.rating || 0);

    let category = "";
    try {
      const rawField = (product.metafields || []).find(
        (m) => m && m.key === "filter_product_category"
      );
      if (rawField?.reference?.fields?.length) {
        const nameField = rawField.reference.fields.find(
          (f) => f.key === "name"
        );
        category = nameField?.value || rawField.reference.handle || "";
      } else if (rawField?.reference?.handle) {
        category = rawField.reference.handle.replace(/-/g, " ");
      } else if (typeof rawField?.value === "string") {
        category = rawField.value;
      } else {
        category = "";
      }
    } catch {
      category = "";
    }

    const basicColor = productMeta.basic_size_variant_image_plp || "";
    const isGrouped = (product.options || []).length === 3;

    const visibleVariants = product.allVariants.filter(({ node }) => {
      const metas = (node.metafields || []).filter(Boolean);
      const hideValue = getMetafieldValue(metas, "hide_on_plp");
      return hideValue !== "true";
    });

    if (isGrouped) {
      const grouped = {};
      for (const { node: variant } of visibleVariants) {
        const options = (variant.selectedOptions || []).map((opt) => opt.value);
        const colorGroup = `${options[0]} / ${options[1]}`;
        if (!grouped[colorGroup]) grouped[colorGroup] = [];
        grouped[colorGroup].push(variant);
      }

      for (const [colorGroup, group] of Object.entries(grouped)) {
        const firstVariant = group[0];
        const price = Number(firstVariant.price.amount);
        const compare = firstVariant.compareAtPrice?.amount
          ? Number(firstVariant.compareAtPrice.amount)
          : null;
        const currency = firstVariant.price.currencyCode;
        const firstVariantMetas = (firstVariant.metafields || []).filter(
          Boolean
        );
        const badges = parseBadgeMetafield(firstVariantMetas);
        let salePercent = 0;
        let saleEuro = 0;

        if (discountValue > 0) {
          saleEuro = discountValue;
          salePercent = Math.round((discountValue * 100) / price);
        } else if (compare && compare > price) {
          saleEuro = compare - price;
          salePercent = Math.round(((compare - price) * 100) / compare);
        }

        const groupObject = {
          product_title: product.title,
          collection_name: productMeta.collection_name || "",
          color_group: colorGroup,
          id: firstVariant.id,
          rating,
          title: firstVariant.title,
          price_sort: price,
          price: formatPrice(price, currency),
          compare: compare ? formatPrice(compare, currency) : "",
          available: firstVariant.availableForSale,
          url: `${window.localePathPrefix || ''}/products/${product.handle}?variant=${firstVariant.id.replace("gid://shopify/ProductVariant/", "")}`,
          sale: salePercent,
          sale_euro: saleEuro,
          basic_color: basicColor,
          badges,
          variants: [],
          product_handle: product.handle,
          category,
          options: (firstVariant.selectedOptions || []).map((o) => o.value),
          price_filter:
            price >= 50000
              ? "500+"
              : price >= 40000
                ? "400-500"
                : price >= 30000
                  ? "300-400"
                  : "300",
          description: (firstVariant.selectedOptions || [])
            .map((o) => o.value)
            .join(" + "),
          image_url: normalizeImage(firstVariant.image?.url),
          size: [],
        };

        groupObject[product.options[0]?.name || "Option1"] =
          (firstVariant.selectedOptions || [])[0]?.value || "";
        groupObject[product.options[1]?.name || "Option2"] =
          (firstVariant.selectedOptions || [])[1]?.value || "";
        groupObject[product.options[2]?.name || "Option3"] =
          (firstVariant.selectedOptions || [])[2]?.value || "";

        for (const variant of group) {
          const basePrice = Number(variant.price.amount);
          const comparePrice = variant.compareAtPrice?.amount
            ? Number(variant.compareAtPrice.amount)
            : null;
          const currencyInner = variant.price.currencyCode;
          const variantMetas = (variant.metafields || []).filter(Boolean);
          const variantBadges = parseBadgeMetafield(variantMetas);
          let salePct = 0;
          let saleAmt = 0;

          if (discountValue > 0) {
            saleAmt = discountValue;
            salePct = Math.round((discountValue * 100) / basePrice);
          } else if (comparePrice && comparePrice > basePrice) {
            saleAmt = comparePrice - basePrice;
            salePct = Math.round(
              ((comparePrice - basePrice) * 100) / comparePrice
            );
          }

          const newestMeta = (
            Array.isArray(variant.metafields) ? variant.metafields : []
          )
            .filter(Boolean)
            .find((m) => m.key === "newest_priority");
          const newestPriority = newestMeta ? Number(newestMeta.value) || 1 : 1;

          groupObject.variants.push({
            variant_id: variant.id,
            variant_newest: newestPriority,
            variant_title:
              (variant.selectedOptions || []).slice(-1)[0]?.value || "",
            variant_title_full: variant.title,
            variant_link: `${window.localePathPrefix || ''}/products/${product.handle}?variant=${variant.id.replace("gid://shopify/ProductVariant/", "")}`,
            image_url: normalizeImage(variant.image?.url),
            variant_price: formatPrice(basePrice, currencyInner),
            variant_compare_price: comparePrice
              ? formatPrice(comparePrice, currencyInner)
              : "",
            badges: variantBadges,
            sale: salePct,
            sale_euro: saleAmt,
            sale_euro_formatted: saleAmt > 0 ? formatPrice(saleAmt, currencyInner) : "",
            available: variant.availableForSale,
          });

          groupObject.size.push(
            (variant.selectedOptions || []).slice(-1)[0]?.value || ""
          );
        }

        allVariants.push(groupObject);
      }
    } else {
      for (const { node: variant } of visibleVariants) {
        const basePrice = Number(variant.price.amount);
        const comparePrice = variant.compareAtPrice?.amount
          ? Number(variant.compareAtPrice.amount)
          : null;
        const currency = variant.price.currencyCode;
        const variantMetas = (variant.metafields || []).filter(Boolean);
        const badges = parseBadgeMetafield(variantMetas);
        let salePct = 0;
        let saleAmt = 0;

        if (discountValue > 0) {
          saleAmt = discountValue;
          salePct = Math.round((discountValue * 100) / basePrice);
        } else if (comparePrice && comparePrice > basePrice) {
          saleAmt = comparePrice - basePrice;
          salePct = Math.round(
            ((comparePrice - basePrice) * 100) / comparePrice
          );
        }

        const newestMeta = (
          Array.isArray(variant.metafields) ? variant.metafields : []
        )
          .filter(Boolean)
          .find((m) => m.key === "newest_priority");
        const newestPriority = newestMeta ? Number(newestMeta.value) || 1 : 1;

        const productObj = {
          id: variant.id,
          rating,
          title: variant.title,
          product_title: product.title,
          collection_name: productMeta.collection_name || "",
          price_sort: basePrice,
          price: formatPrice(basePrice, currency),
          compare: comparePrice ? formatPrice(comparePrice, currency) : "",
          available: variant.availableForSale,
          url: `${window.localePathPrefix || ''}/products/${product.handle}?variant=${variant.id.replace("gid://shopify/ProductVariant/", "")}`,
          sale: salePct,
          sale_euro: saleAmt,
          sale_euro_formatted: saleAmt > 0 ? formatPrice(saleAmt, currency) : "",
          badges,
          product_handle: product.handle,
          variant_newest: newestPriority,
          category,
          options: (variant.selectedOptions || []).map((o) => o.value),
          price_filter:
            basePrice >= 50000
              ? "500+"
              : basePrice >= 40000
                ? "400-500"
                : basePrice >= 30000
                  ? "300-400"
                  : "300",
          description: (variant.selectedOptions || [])
            .map((o) => o.value)
            .join(" + "),
          image_url: normalizeImage(variant.image?.url),
          size: [(variant.selectedOptions || []).slice(-1)[0]?.value || ""],
        };

        productObj[product.options[0]?.name || "Option1"] =
          (variant.selectedOptions || [])[0]?.value || "";
        productObj[product.options[1]?.name || "Option2"] =
          (variant.selectedOptions || [])[1]?.value || "";
        productObj[product.options[2]?.name || "Option3"] =
          (variant.selectedOptions || [])[2]?.value || "";

        allVariants.push(productObj);
      }
    }
  }

  return allVariants;
}

(async () => {
  try {
    const products = await fetchAllCollectionProducts(window.collectionHandle);
    window.variants = buildVariantsArray(products);
    console.log(
      "✅ Loaded",
      window.variants.length,
      "variants (Liquid-compatible + Currency + Badge)"
    );
    document.dispatchEvent(
      new CustomEvent("variants:ready", { detail: window.variants })
    );
  } catch (error) {
    console.error("❌ Error loading variants:", error);
  }
})();
